import React, { useEffect, useState, useCallback, useRef } from "react";
import { auth, db } from "../../firebase";
import { Database, RefreshCw, TrendingUp, Clock, Package, DollarSign, Zap, Activity, ArrowUp } from "lucide-react";
import cashingSfx from "../../assets/cashing.mp3";
import { collection, getDocs } from "firebase/firestore";

const SUCURSAL_PREFIX = "pf_";
const COLLECTION_NAME = "ventas_desglosadas";
const DEFAULT_LIMIT = 1000;

const LiveStats = () => {
    const [loading, setLoading] = useState(false);
    const [databases, setDatabases] = useState([]);
    const [dataByBranch, setDataByBranch] = useState({});
    const [combined, setCombined] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState("");
    const [query, setQuery] = useState("");
    const [filterToday, setFilterToday] = useState(true);
    const [granularity, setGranularity] = useState("hour");
    const [selectedHourIdx, setSelectedHourIdx] = useState(23);
    const [expanded, setExpanded] = useState({});
    const [error, setError] = useState(null);
    const [lastSeenByBranch, setLastSeenByBranch] = useState({});
    const [recentNewOrder, setRecentNewOrder] = useState(null);
    const audioRef = useRef(null);
    const [productsByCode, setProductsByCode] = useState({});
    const [productsByName, setProductsByName] = useState({});

    const FUNCTIONS_BASE_URL = "https://us-central1-newpollofeliz.cloudfunctions.net";

    const getAuthToken = useCallback(async () => {
        try {
            const user = auth.currentUser;
            if (!user) throw new Error("No hay usuario autenticado");
            return await user.getIdToken();
        } catch (e) {
            console.error(e);
            return null;
        }
    }, []);

    const fetchDatabases = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = await getAuthToken();
            if (!token) throw new Error("No se pudo obtener token");
            const res = await fetch(`${FUNCTIONS_BASE_URL}/listDatabases`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                body: JSON.stringify({}),
            });
            const json = await res.json();
            if (!json.success) throw new Error(json.error || "Error listando bases de datos");
            const branches = json.databases
                .map((d) => d.name)
                .filter((n) => n && n.startsWith(SUCURSAL_PREFIX));
            setDatabases(branches);
            return branches;
        } catch (e) {
            setError(e.message);
            return [];
        } finally {
            setLoading(false);
        }
    }, [getAuthToken]);

    const fetchBranchData = useCallback(
        async (branchName, limit = DEFAULT_LIMIT) => {
            try {
                const token = await getAuthToken();
                if (!token) return;
                const res = await fetch(`${FUNCTIONS_BASE_URL}/getData`, {
                    method: "POST",
                    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                    body: JSON.stringify({
                        database: branchName,
                        collection: COLLECTION_NAME,
                        limit,
                        filter: {},
                        sort: { fecha: -1 },
                    }),
                });
                const json = await res.json();
                if (!json.success) throw new Error(json.error || `Error obteniendo datos de ${branchName}`);
                setDataByBranch((prev) => ({ ...prev, [branchName]: json.data || [] }));
            } catch (e) {
                console.error(e);
            }
        },
        [getAuthToken]
    );

    const fetchLatestBatch = useCallback(
        async (branchName, limit = 50) => {
            try {
                const token = await getAuthToken();
                if (!token) return [];
                const res = await fetch(`${FUNCTIONS_BASE_URL}/getData`, {
                    method: "POST",
                    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                    body: JSON.stringify({
                        database: branchName,
                        collection: COLLECTION_NAME,
                        limit,
                        filter: {},
                        sort: { fecha: -1 },
                    }),
                });
                const json = await res.json();
                if (!json.success) return [];
                return json.data || [];
            } catch (e) {
                return [];
            }
        },
        [getAuthToken]
    );

    const refreshAll = useCallback(async () => {
        const branches = await fetchDatabases();
        for (const b of branches) {
            await fetchBranchData(b, DEFAULT_LIMIT);
        }
    }, [fetchDatabases, fetchBranchData]);

    useEffect(() => {
        refreshAll();
    }, [refreshAll]);

    useEffect(() => {
        audioRef.current = new Audio(cashingSfx);
        audioRef.current.volume = 0.6;
    }, []);

    // Load products once for importe calculation
    useEffect(() => {
        const loadProducts = async () => {
            try {
                const snap = await getDocs(collection(db, "productos"));
                const byCode = {};
                const byName = {};
                snap.forEach((d) => {
                    const p = d.data() || {};
                    const code = (p.codigo || d.id || "").toString();
                    const name = (p.nombre || "").toString();
                    if (code) byCode[code] = p;
                    if (name) byName[name.toLowerCase()] = p;
                });
                setProductsByCode(byCode);
                setProductsByName(byName);
            } catch (_) { }
        };
        loadProducts();
    }, []);

    const playCash = useCallback(() => {
        if (!audioRef.current) return;
        try {
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(() => { });
        } catch (_) { }
    }, []);

    useEffect(() => {
        if (!databases || databases.length === 0) return;
        let isCancelled = false;
        const interval = setInterval(async () => {
            if (isCancelled) return;
            try {
                for (const branch of databases) {
                    const latestBatch = await fetchLatestBatch(branch, 50);
                    if (!Array.isArray(latestBatch) || latestBatch.length === 0) continue;

                    const currentLast = lastSeenByBranch[branch] ?? (() => {
                        const rows = Array.isArray(dataByBranch[branch]) ? dataByBranch[branch] : [];
                        if (!rows.length) return 0;
                        return Math.max(...rows.filter((r) => r.fecha).map((r) => new Date(r.fecha).getTime()));
                    })();

                    const todayStart = startOfToday();
                    const todayEnd = endOfToday();
                    const newOnes = latestBatch.filter((r) => {
                        const t = r.fecha ? new Date(r.fecha).getTime() : 0;
                        if (filterToday && (t < todayStart || t > todayEnd)) return false;
                        return t > currentLast;
                    });

                    if (newOnes.length > 0) {
                        setRecentNewOrder({ ...newOnes[0], branch });
                        setTimeout(() => setRecentNewOrder(null), 3000);
                        setDataByBranch((prev) => {
                            const existing = Array.isArray(prev[branch]) ? prev[branch] : [];
                            const merged = [...newOnes, ...existing].slice(0, DEFAULT_LIMIT);
                            return { ...prev, [branch]: merged };
                        });
                        const newLast = Math.max(currentLast, ...newOnes.map((r) => (r.fecha ? new Date(r.fecha).getTime() : 0)));
                        setLastSeenByBranch((prev) => ({ ...prev, [branch]: newLast }));
                        playCash();
                    }
                }
            } catch (_) { }
        }, 3000);
        return () => {
            isCancelled = true;
            clearInterval(interval);
        };
    }, [databases, fetchLatestBatch, filterToday, dataByBranch, lastSeenByBranch, playCash]);

    useEffect(() => {
        const rows = [];
        Object.entries(dataByBranch).forEach(([branch, rowsArr]) => {
            if (Array.isArray(rowsArr)) {
                rowsArr.forEach((r) => {
                    rows.push({ ...r, sucursal: branch });
                });
            }
        });
        rows.sort((a, b) => {
            const da = a.fecha ? new Date(a.fecha).getTime() : 0;
            const db = b.fecha ? new Date(b.fecha).getTime() : 0;
            return db - da;
        });
        setCombined(rows);
    }, [dataByBranch]);

    const startOfToday = () => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d.getTime();
    };

    const endOfToday = () => {
        const d = new Date();
        d.setHours(23, 59, 59, 999);
        return d.getTime();
    };

    const filteredCombined = combined.filter((r) => {
        const okBranch = selectedBranch ? r.sucursal === selectedBranch : true;
        const text = (r.descripcion || "") + " " + (r.codigo || "");
        const okText = query ? text.toLowerCase().includes(query.toLowerCase()) : true;
        const okDate = (() => {
            if (!filterToday) return true;
            if (!r.fecha) return false;
            const t = new Date(r.fecha).getTime();
            return t >= startOfToday() && t <= endOfToday();
        })();
        return okBranch && okText && okDate;
    });

    const totalRegistros = filteredCombined.length;
    const porSucursal = databases.reduce((acc, s) => {
        acc[s] = filteredCombined.filter((r) => r.sucursal === s).length;
        return acc;
    }, {});

    const topItems = (() => {
        const map = new Map();
        for (const r of filteredCombined) {
            const key = r.descripcion || "(Sin descripción)";
            map.set(key, (map.get(key) || 0) + (Number(r.cantidad) || 0));
        }
        return [...map.entries()]
            .sort((a, b) => b[1] - a[1])
            .slice(0, 8)
            .map(([descripcion, qty]) => ({ descripcion, qty }));
    })();

    const hourlyBuckets = (() => {
        if (filterToday) {
            const todayStart = startOfToday();
            const hours = Array.from({ length: 24 }, (_, h) => {
                const start = todayStart + h * 3600000;
                const label = `${String(h).padStart(2, "0")}:00`;
                const end = start + 3600000;
                const c = filteredCombined.filter(
                    (r) => r.fecha && new Date(r.fecha).getTime() >= start && new Date(r.fecha).getTime() < end
                ).length;
                return { label, value: c };
            });
            return hours;
        }
        const now = Date.now();
        const hours = Array.from({ length: 24 }, (_, i) => {
            const t = new Date(now - (23 - i) * 3600000);
            return {
                label: `${String(t.getHours()).padStart(2, "0")}:00`,
                start: new Date(t.getFullYear(), t.getMonth(), t.getDate(), t.getHours(), 0, 0).getTime(),
            };
        });
        const counts = hours.map((h, idx) => {
            const end = idx < hours.length - 1 ? hours[idx + 1].start : now;
            const c = filteredCombined.filter((r) => r.fecha && new Date(r.fecha).getTime() >= h.start && new Date(r.fecha).getTime() < end).length;
            return { label: h.label, value: c };
        });
        return counts;
    })();

    const minuteBuckets = (() => {
        let baseStart;
        if (filterToday) {
            const d = new Date();
            d.setHours(0, 0, 0, 0);
            baseStart = d.getTime() + selectedHourIdx * 3600000;
        } else {
            const now = Date.now();
            const hourStart = new Date(now - (23 - selectedHourIdx) * 3600000);
            baseStart = new Date(
                hourStart.getFullYear(),
                hourStart.getMonth(),
                hourStart.getDate(),
                hourStart.getHours(),
                0,
                0
            ).getTime();
        }
        return Array.from({ length: 60 }, (_, m) => {
            const start = baseStart + m * 60000;
            const end = start + 60000;
            const c = filteredCombined.filter(
                (r) => r.fecha && new Date(r.fecha).getTime() >= start && new Date(r.fecha).getTime() < end
            ).length;
            return { label: `${String(m).padStart(2, "0")}`, value: c };
        });
    })();

    const peakHour = hourlyBuckets.length
        ? hourlyBuckets.reduce((a, b) => (b.value > a.value ? b : a))
        : { label: "-", value: 0 };

    const peakMinute = minuteBuckets.length
        ? minuteBuckets.reduce((a, b) => (b.value > a.value ? b : a))
        : { label: "-", value: 0 };

    const parseImporte = (v) => {
        if (v === null || v === undefined) return 0;
        if (typeof v === "number") return v;
        const n = Number(String(v).replace(/[$,\s]/g, ""));
        return isNaN(n) ? 0 : n;
    };

    const getUnitPrice = (row) => {
        const code = (row.codigo || "").toString();
        const nameKey = (row.descripcion || row.nombre || "").toString().toLowerCase();
        if (code && productsByCode[code] && typeof productsByCode[code].pVenta === "number") return productsByCode[code].pVenta;
        if (nameKey && productsByName[nameKey] && typeof productsByName[nameKey].pVenta === "number") return productsByName[nameKey].pVenta;
        return 0;
    };
    const resolveImporte = (row) => {
        const existing = parseImporte(row.importe);
        if (existing > 0) return existing;
        const qty = Number(row.cantidad) || 0;
        const unit = getUnitPrice(row);
        return qty * unit;
    };
    const totalImporte = filteredCombined.reduce((sum, r) => sum + resolveImporte(r), 0);
    const uniqueItems = new Set(filteredCombined.map((r) => r.descripcion || "(Sin descripción)")).size;
    const dateRange = (() => {
        if (filteredCombined.length === 0) return { min: null, max: null, minutes: 0 };
        const times = filteredCombined.map((r) => (r.fecha ? new Date(r.fecha).getTime() : 0));
        const min = new Date(Math.min(...times));
        const max = new Date(Math.max(...times));
        const minutes = Math.max(1, Math.round((max.getTime() - min.getTime()) / 60000));
        return { min, max, minutes };
    })();

    const salesPerMinute = dateRange.minutes > 0 ? (filteredCombined.length / dateRange.minutes).toFixed(2) : 0;
    const lastHourCount = filteredCombined.filter((r) => r.fecha && (new Date(r.fecha).getTime() >= Date.now() - 60 * 60000)).length;
    const topBranch = (() => {
        const entries = Object.entries(porSucursal).sort((a, b) => b[1] - a[1]);
        return entries.length ? { name: entries[0][0], count: entries[0][1] } : { name: "-", count: 0 };
    })();

    const avgImporte = totalRegistros > 0 ? (totalImporte / totalRegistros).toFixed(2) : 0;
    const lastOrderTime = combined.length > 0 ? new Date(combined[0].fecha).getTime() : null;
    const timeSinceLastOrder = lastOrderTime ? Math.round((Date.now() - lastOrderTime) / 1000) : null;

    return (
        <div className="min-h-screen bg-white p-4">
            {recentNewOrder && (
                <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top-2">
                    <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl shadow-2xl p-4 border border-green-400/30 max-w-sm">
                        <div className="flex items-center gap-2 mb-2">
                            <Zap className="w-5 h-5 animate-pulse" />
                            <p className="font-bold">¡Nueva Orden!</p>
                        </div>
                        <p className="text-sm">{recentNewOrder.descripcion} x{recentNewOrder.cantidad}</p>
                        <p className="text-xs text-green-100 mt-1">{recentNewOrder.branch}</p>
                    </div>
                </div>
            )}

            <div className="max-w-7xl mx-auto space-y-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center shadow-lg">
                            <Database className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-black text-gray-900">LiveStats</h1>
                            <p className="text-sm text-gray-600">Ventas en Tiempo Real • {databases.length} Sucursales Activas</p>
                        </div>
                    </div>
                    <button
                        onClick={refreshAll}
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-lg hover:from-orange-600 hover:to-red-700 disabled:opacity-50 transition font-semibold shadow-lg"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                        Refrescar
                    </button>
                </div>

                {error && (
                    <div className="p-4 rounded-xl bg-red-100 text-red-700 border border-red-300 text-sm font-medium">{error}</div>
                )}

                <div className="bg-white rounded-xl border border-gray-200 shadow p-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-2">Sucursal</label>
                            <select
                                value={selectedBranch}
                                onChange={(e) => setSelectedBranch(e.target.value)}
                                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-orange-500"
                            >
                                <option value="">Todas</option>
                                {databases.map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-xs font-semibold text-gray-700 mb-2">Buscar artículo o código</label>
                            <input
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="POLLO ENTERO, UE01..."
                                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-500"
                            />
                        </div>
                        <div>
                            <button
                                onClick={() => setFilterToday((v) => !v)}
                                className={`w-full px-3 py-2 rounded-lg text-sm font-semibold border transition ${filterToday ? "bg-orange-600 text-white border-orange-600" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}
                            >
                                {filterToday ? "📅 Hoy" : "📊 Todo"}
                            </button>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200 p-4 hover:border-blue-300 transition shadow">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-semibold text-blue-800">REGISTROS</p>
                            <Package className="w-4 h-4 text-blue-600" />
                        </div>
                        <p className="text-2xl font-black text-gray-900">{totalRegistros}</p>
                        <p className="text-[10px] text-blue-700 mt-1">Filtrados</p>
                    </div>
                    <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl border border-emerald-200 p-4 hover:border-emerald-300 transition shadow">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-semibold text-emerald-800">VELOCIDAD</p>
                            <TrendingUp className="w-4 h-4 text-emerald-600" />
                        </div>
                        <p className="text-2xl font-black text-gray-900">{salesPerMinute}</p>
                        <p className="text-[10px] text-emerald-700 mt-1">ord/min</p>
                    </div>
                    <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl border border-yellow-200 p-4 hover:border-yellow-300 transition shadow">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-semibold text-yellow-800">ÚLTIMAS 60 MIN</p>
                            <Clock className="w-4 h-4 text-yellow-600" />
                        </div>
                        <p className="text-2xl font-black text-gray-900">{lastHourCount}</p>
                        <p className="text-[10px] text-yellow-700 mt-1">Recientes</p>
                    </div>
                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl border border-orange-200 p-4 hover:border-orange-300 transition shadow">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-semibold text-orange-800">IMPORTE TOTAL</p>
                            <DollarSign className="w-4 h-4 text-orange-600" />
                        </div>
                        <p className="text-2xl font-black text-gray-900">${totalImporte.toLocaleString()}</p>
                        <p className="text-[10px] text-orange-700 mt-1">Suma</p>
                    </div>
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border border-purple-200 p-4 hover:border-purple-300 transition shadow">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-semibold text-purple-800">PROMEDIO</p>
                            <Activity className="w-4 h-4 text-purple-600" />
                        </div>
                        <p className="text-2xl font-black text-gray-900">${avgImporte}</p>
                        <p className="text-[10px] text-purple-700 mt-1">Por orden</p>
                    </div>
                    <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-xl border border-pink-200 p-4 hover:border-pink-300 transition shadow">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-semibold text-pink-800">TOP SUCURSAL</p>
                            <ArrowUp className="w-4 h-4 text-pink-600" />
                        </div>
                        <p className="text-lg font-black text-gray-900 truncate">{topBranch.name.replace("pf_", "").toUpperCase()}</p>
                        <p className="text-[10px] text-pink-700 mt-1">{topBranch.count} órdenes</p>
                    </div>
                    <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl border border-teal-200 p-4 hover:border-teal-300 transition shadow">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-semibold text-teal-800">ÚLTIMOS 5 MIN</p>
                        </div>
                        <p className="text-2xl font-black text-gray-900">{filteredCombined.filter((r) => r.fecha && new Date(r.fecha).getTime() >= Date.now() - 5 * 60000).length}</p>
                        <p className="text-[10px] text-teal-700 mt-1">órdenes</p>
                    </div>
                    <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl border border-indigo-200 p-4 hover:border-indigo-300 transition shadow">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-semibold text-indigo-800">PROM/HORA</p>
                        </div>
                        <p className="text-2xl font-black text-gray-900">{(() => { const vals = hourlyBuckets.map(b => b.value); const sum = vals.reduce((a, b) => a + b, 0); const avg = sum / Math.max(1, vals.length); return avg.toFixed(2); })()}</p>
                        <p className="text-[10px] text-indigo-700 mt-1">promedio</p>
                    </div>
                </div>

                {/* Ventas por minuto (principal) */}
                <div className="bg-white rounded-xl border border-gray-200 shadow p-4">
                    <div className="flex items-center justify-between mb-2">
                        <div>
                            <p className="text-sm font-bold text-gray-900">⏱️ Ventas por Minuto</p>
                            <p className="text-[11px] text-gray-600">Hora actual: {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <label className="text-xs text-gray-600">Hora:</label>
                            <select value={selectedHourIdx} onChange={(e) => setSelectedHourIdx(Number(e.target.value))} className="px-2 py-1 border border-gray-300 rounded text-xs">
                                {hourlyBuckets.map((h, idx) => (<option key={idx} value={idx}>{h.label}</option>))}
                            </select>
                        </div>
                    </div>
                    <div className="mb-3">
                        <input type="range" min={0} max={23} step={1} value={selectedHourIdx} onChange={(e) => setSelectedHourIdx(Number(e.target.value))} className="w-full accent-orange-600" />
                        <div className="flex justify-between text-[10px] text-gray-500 mt-1">
                            <span>00:00</span>
                            <span>{hourlyBuckets[selectedHourIdx]?.label}</span>
                            <span>23:00</span>
                        </div>
                    </div>
                    <div className="flex items-end gap-[1px] h-32 mb-2 overflow-x-auto pb-2">
                        {minuteBuckets.map((b, idx) => {
                            const max = Math.max(1, ...minuteBuckets.map((x) => x.value));
                            const h = Math.round((b.value / max) * 120) + 4;
                            return (
                                <div key={idx} className="w-[calc(100%/60-1px)] flex-shrink-0">
                                    <div className="w-full bg-gradient-to-t from-amber-500 to-yellow-300 rounded-t" style={{ height: h }} title={`${b.label}': ${b.value}`} />
                                </div>
                            );
                        })}
                    </div>
                    <div className="flex justify-between text-[10px] text-gray-600"><span>00'</span><span>59'</span></div>
                    <div className="mt-3 grid grid-cols-2 gap-2">
                        <div className="p-2 bg-gray-100 rounded text-xs text-gray-800 font-semibold">⚡ Pico Minuto: {peakMinute.label}' ({peakMinute.value})</div>
                        <div className="p-2 bg-gray-100 rounded text-xs text-gray-800 font-semibold">🕒 Hora Actual: {filteredCombined.filter((r) => r.fecha && new Date(r.fecha).getTime() >= (new Date(new Date().setMinutes(0, 0, 0))).getTime()).length}</div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow p-4">
                        <p className="text-sm font-bold text-gray-900 mb-4">📊 Ventas por Hora ({filterToday ? "Hoy" : "Últimas 24h"})</p>
                        <div className="flex items-end gap-1 h-32 mb-2">
                            {hourlyBuckets.map((b, idx) => {
                                const max = Math.max(1, ...hourlyBuckets.map((x) => x.value));
                                const h = Math.round((b.value / max) * 128) + 8;
                                return (
                                    <div key={idx} className="flex-1 group">
                                        <div
                                            className={`w-full rounded-t transition cursor-pointer ${idx === selectedHourIdx ? "bg-gradient-to-t from-blue-500 to-blue-300 shadow-lg" : "bg-gradient-to-t from-red-500 to-orange-400 hover:shadow-lg"}`}
                                            style={{ height: h }}
                                            title={`${b.label}: ${b.value}`}
                                            onClick={() => setSelectedHourIdx(idx)}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                        <div className="flex justify-between text-[10px] text-gray-600">
                            <span>00:00</span>
                            <span>23:00</span>
                        </div>
                        <div className="mt-3 p-2 bg-gray-100 rounded text-xs text-gray-800 font-semibold">
                            ⚡ Pico: {peakHour.label} ({peakHour.value} órdenes)
                        </div>
                    </div>

                    <div className="bg-white rounded-xl border border-gray-200 shadow p-4">
                        <p className="text-sm font-bold text-gray-900 mb-4">🏪 Distribución Sucursales</p>
                        <div className="space-y-2">
                            {databases.map((s) => {
                                const v = porSucursal[s] || 0;
                                const max = Math.max(1, ...Object.values(porSucursal));
                                const w = `${Math.round((v / max) * 100)}%`;
                                return (
                                    <div key={s} className="text-xs">
                                        <div className="flex justify-between mb-1">
                                            <span className="text-gray-800 font-semibold truncate">{s.replace("pf_", "").toUpperCase()}</span>
                                            <span className="text-gray-600">{v}</span>
                                        </div>
                                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                            <div className="h-2 bg-gradient-to-r from-yellow-400 to-red-500 rounded-full transition" style={{ width: w }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="bg-white rounded-xl border border-gray-200 shadow p-4">
                        <div className="flex items-center justify-between mb-4">
                            <p className="text-sm font-bold text-gray-900">⏱️ Desglose Minuto (Hora: {hourlyBuckets[selectedHourIdx]?.label})</p>
                            <button onClick={() => setGranularity(granularity === "minute" ? "hour" : "minute")} className="text-xs px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded text-gray-800">
                                {granularity === "minute" ? "← Volver" : "Detalle →"}
                            </button>
                        </div>
                        {granularity === "minute" && (
                            <>
                                <div className="flex items-end gap-[1px] h-24 mb-2 overflow-x-auto pb-2">
                                    {minuteBuckets.map((b, idx) => {
                                        const max = Math.max(1, ...minuteBuckets.map((x) => x.value));
                                        const h = Math.round((b.value / max) * 96) + 4;
                                        return (
                                            <div key={idx} className="w-[calc(100%/60-1px)] flex-shrink-0">
                                                <div className="w-full bg-gradient-to-t from-amber-500 to-yellow-300 rounded-t" style={{ height: h }} title={`${b.label}': ${b.value}`} />
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="flex justify-between text-[10px] text-gray-600">
                                    <span>00'</span>
                                    <span>59'</span>
                                </div>
                                <div className="mt-3 p-2 bg-gray-100 rounded text-xs text-gray-800 font-semibold">
                                    ⚡ Pico Minuto: {peakMinute.label}' ({peakMinute.value} órdenes)
                                </div>
                            </>
                        )}
                    </div>

                    <div className="bg-white rounded-xl border border-gray-200 shadow p-4">
                        <p className="text-sm font-bold text-gray-900 mb-4">🍗 Top Artículos (por cantidad)</p>
                        <div className="space-y-2">
                            {topItems.map((t, idx) => {
                                const maxQty = Math.max(...topItems.map(x => x.qty));
                                const w = `${Math.round((t.qty / maxQty) * 100)}%`;
                                return (
                                    <div key={t.descripcion} className="text-xs">
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-gray-800 font-semibold flex items-center gap-2">
                                                <span className="text-orange-500 font-bold">#{idx + 1}</span>
                                                <span className="truncate">{t.descripcion}</span>
                                            </span>
                                            <span className="text-gray-700 font-bold">{t.qty}</span>
                                        </div>
                                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                            <div className="h-2 bg-gradient-to-r from-green-400 to-emerald-600 rounded-full transition" style={{ width: w }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-white rounded-xl border border-gray-200 shadow p-3">
                        <p className="text-xs text-gray-600 font-semibold">ARTÍCULOS ÚNICOS</p>
                        <p className="text-xl font-black text-gray-900">{uniqueItems}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 shadow p-3">
                        <p className="text-xs text-gray-600 font-semibold">RANGO TEMPORAL</p>
                        <p className="text-xs text-gray-800 font-semibold">{dateRange.minutes} min</p>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 shadow p-3">
                        <p className="text-xs text-gray-600 font-semibold">ÚLTIMA ORDEN</p>
                        <p className="text-xs text-gray-800 font-semibold">{timeSinceLastOrder ? `hace ${timeSinceLastOrder}s` : "-"}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 shadow p-3">
                        <p className="text-xs text-gray-600 font-semibold">ESTADO</p>
                        <p className="text-xs font-semibold text-green-600">🟢 EN VIVO</p>
                    </div>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 shadow overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                        <h3 className="text-sm font-bold text-gray-900">📋 Últimas Órdenes</h3>
                        <p className="text-xs text-gray-600">Ordenadas por fecha (más recientes primero)</p>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead className="bg-gray-50 text-gray-700 border-b border-gray-200">
                                <tr>
                                    <th className="px-4 py-3 text-left font-bold">HORA</th>
                                    <th className="px-4 py-3 text-left font-bold">SUCURSAL</th>
                                    <th className="px-4 py-3 text-left font-bold">ARTÍCULO</th>
                                    <th className="px-4 py-3 text-center font-bold">QTY</th>
                                    <th className="px-4 py-3 text-right font-bold">MONTO</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {filteredCombined.slice(0, 150).map((r, idx) => (
                                    <tr
                                        key={idx}
                                        className="hover:bg-gray-50 transition border-b border-gray-100"
                                    >
                                        <td className="px-4 py-3 text-gray-700">
                                            {r.fecha ? new Date(r.fecha).toLocaleTimeString() : "-"}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="inline-block px-2 py-1 rounded-full text-white bg-orange-500 font-semibold text-xs">
                                                {r.sucursal.replace("pf_", "").toUpperCase()}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-gray-800 font-medium truncate max-w-[300px]" title={r.descripcion}>
                                            {r.descripcion || "-"}
                                        </td>
                                        <td className="px-4 py-3 text-center text-gray-800 font-bold">{r.cantidad || "-"}</td>
                                        <td className="px-4 py-3 text-right text-orange-600 font-bold">
                                            ${resolveImporte(r).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LiveStats;